/** @format */

import * as React from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import CreateTicketModal from './createTicketModal';
import TicketItem, { TicketsListSkeleton } from './ticketItem';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import UpdatePostalCodeModal from './updatePostalCodeModal';
import { useDispatch, useSelector } from 'src/store';
import UpdateTicketModal from './updateTicketModal';
import { TICKET_MODAL } from 'src/constants';
import CloseTicketModal from './closeTicketModal';
import CreateTicketForTollingModal from './createTicketForTollingModal';
import CreateTicketForMoDModal from './createTicketForMoDModal';
import SelectTicketTypeModal from './selectTicketTypeModal';
import { getTickets } from 'src/slices/tickets';
import { useState, useEffect, useRef } from 'react';
import { TicketSearch } from 'src/components/ticket';
import { Tooltip } from 'src/components/tooltip';

const startViewTransition = callback => {
  if (document.startViewTransition) {
    document.startViewTransition(() => {
      flushSync(callback);
    });
  } else {
    callback();
  }
};

export default function TicketsWidget({ data }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [modalOpened, setModalOpened] = useState(null);
  const [currentUpdateTicketId, setCurrentUpdateTicketId] = useState(null);
  const [selectedTicketType, setSelectedTicketType] = useState(null); // 'fleet' | 'tolling' | null
  const { userData } = useSelector(state => state.users);
  const { ticketLoading } = useSelector(state => state.tickets);
  const { tickets } = useSelector(state => state.tickets);

  const isLoading = ticketLoading && tickets === null;
  const [displayedContent, setDisplayedContent] = useState({ loading: isLoading, tickets: tickets });
  const prevLoadingRef = useRef(isLoading);

  useEffect(() => {
    if (tickets === null) {
      dispatch(getTickets());
    }
  }, [tickets, dispatch]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (wasLoading && !isLoading) {
      startViewTransition(() => {
        setDisplayedContent({ loading: false, tickets });
      });
    } else if (isLoading && !wasLoading) {
      setDisplayedContent({ loading: true, tickets: null });
    } else if (!isLoading) {
      setDisplayedContent({ loading: false, tickets });
    }
  }, [isLoading, tickets]);

  const isTollingCustomer = userData && ['tolling-proxy-customer', 'tolling-customer'].includes(userData.scenario);

  const handleOpenCreateTicketModal = () => {
    if (!userData?.address?.postalCode || !userData?.company) {
      setModalOpened(TICKET_MODAL.UPDATE_POSTALCODE);
    } else if (isTollingCustomer) {
      setSelectedTicketType(null);
      setModalOpened(TICKET_MODAL.TOLLING_CUSTOMER_SELECTION);
    } else {
      setModalOpened(TICKET_MODAL.CREATE_TICKET);
    }
  };

  const handleSelectTicketType = type => {
    setSelectedTicketType(type);
    setModalOpened(TICKET_MODAL.CREATE_TICKET);
  };

  const handleCloseCreateTicketModal = () => {
    setModalOpened(null);
    setSelectedTicketType(null);
  };
  const toggleUpdateTicketModal = incidentid => {
    if (modalOpened === TICKET_MODAL.UPDATE_TICKET) {
      setModalOpened(null);
      setCurrentUpdateTicketId(null);
    } else {
      setModalOpened(TICKET_MODAL.UPDATE_TICKET);
      setCurrentUpdateTicketId(incidentid);
    }
  };
  const toggleCancelTicketModal = incidentid => {
    if (modalOpened === TICKET_MODAL.CLOSE_TICKET) {
      setModalOpened(null);
      setCurrentUpdateTicketId(null);
    } else {
      setModalOpened(TICKET_MODAL.CLOSE_TICKET);
      setCurrentUpdateTicketId(incidentid);
    }
  };

  return (
    <div className="tickets-widget" data-width={data.w} data-height={data.h}>
      <div className="tickets-container">
        <div className="tickets-header">
          <h3 className="tickets-heading" data-name="">
            {t('Tickets.yourSuportServices')}
          </h3>
          <TicketSearch />
        </div>

        {/* <div style={{ viewTransitionName: 'tickets-list' }}> */}
        <div>
          {displayedContent.loading ? (
            <TicketsListSkeleton count={5} />
          ) : (
            <>
              {displayedContent.tickets && displayedContent.tickets.length > 0 ? (
                <ol className="tickets-table">
                  <li className="ticket ticket-container">
                    <div className="attribute">{t('Tickets.caseID')}</div>
                    <div className="attribute">{t('Tickets.title')}</div>
                    <div className="attribute">{t('Tickets.submitted')}</div>
                    <div className="attribute">{t('Tickets.status')}</div>
                    <div className="attribute"></div>
                  </li>
                  {[...displayedContent.tickets]
                    .sort((a, b) => new Date(b.createdon) - new Date(a.createdon))
                    .map((ticket, i) => (
                      <TicketItem
                        ticket={ticket}
                        key={ticket.incidentid + i}
                        toggleUpdateTicketModal={toggleUpdateTicketModal}
                        toggleCancelTicketModal={toggleCancelTicketModal}
                      />
                    ))}
                </ol>
              ) : (
                <p className="tickets-widget--list-empty">{t('widget.noTickets')}</p>
              )}
            </>
          )}
        </div>
        {!displayedContent.loading && (
          <div className="tickets-links">
            <Link to={`/mycases`} className="tickets-link-all">
              <span>{t('Tickets.allCases')}</span>
            </Link>
            <Link to={`/mycompanycases`} className="tickets-link-all">
              <span>{t('Tickets.myCompanyCases')}</span>
            </Link>
            <Link to={`#`} className="tickets-link-all" onClick={handleOpenCreateTicketModal}>
              <span>{t('Tickets.newSupportCase')}</span>
            </Link>
          </div>
        )}
      </div>
      {isTollingCustomer ? (
        <>
          <SelectTicketTypeModal
            open={modalOpened === TICKET_MODAL.TOLLING_CUSTOMER_SELECTION}
            onClose={() => setModalOpened(null)}
            onSelectFleet={() => handleSelectTicketType('fleet')}
            onSelectTolling={() => handleSelectTicketType('tolling')}
          />
          <CreateTicketModal
            open={modalOpened === TICKET_MODAL.CREATE_TICKET && selectedTicketType === 'fleet'}
            onClose={handleCloseCreateTicketModal}
          />
          <CreateTicketForTollingModal
            open={modalOpened === TICKET_MODAL.CREATE_TICKET && selectedTicketType === 'tolling'}
            onClose={handleCloseCreateTicketModal}
          />
        </>
      ) : userData && userData.scenario && userData.scenario.includes('maps-on-demand') ? (
        <CreateTicketForMoDModal
          open={modalOpened === TICKET_MODAL.CREATE_TICKET}
          onClose={() => setModalOpened(null)}
        />
      ) : (
        userData && (
          <CreateTicketModal open={modalOpened === TICKET_MODAL.CREATE_TICKET} onClose={() => setModalOpened(null)} />
        )
      )}
      <UpdatePostalCodeModal
        open={modalOpened === TICKET_MODAL.UPDATE_POSTALCODE}
        onClose={() => setModalOpened(null)}
        onUpdatePostalCodeSuccess={() =>
          setModalOpened(isTollingCustomer ? TICKET_MODAL.TOLLING_CUSTOMER_SELECTION : TICKET_MODAL.CREATE_TICKET)
        }
      />
      <UpdateTicketModal
        incidentid={currentUpdateTicketId}
        open={modalOpened === TICKET_MODAL.UPDATE_TICKET}
        onClose={() => {
          setModalOpened(null);
          toggleUpdateTicketModal(null);
        }}
      />
      <CloseTicketModal
        open={modalOpened === TICKET_MODAL.CLOSE_TICKET}
        onClose={() => {
          setModalOpened(null);
          toggleCancelTicketModal(null);
        }}
        incidentid={currentUpdateTicketId}
      />

      {data.tooltip && (
        <div className="tickets-widget--list-item-tooltip">
          <Tooltip content={data.tooltip} placement="bottom" />
        </div>
      )}
    </div>
  );
}
